import { CdkDrag } from "@angular/cdk/drag-drop";

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { CheckboxInputComponent } from "rl-common/components/checkbox-input/checkbox-input.component";
import { ICharacteristicMetaData } from "rl-common/models/i-characteristic-meta-data";
import { ICharacteristicMetaDataCollection } from "rl-common/models/i-characteristic-meta-data-collection";
import { Subscription } from "rxjs";
import { AppendWithDirective } from "rl-common/components/char-data/directives/append-with.directive";
import { ChipComponent } from "rl-common/components/chip/chip.component";
import { TextInputComponent } from "rl-common/components/text/text-input/text-input.component";
import { NewTabInModalDirective } from "rl-common/directives/new-tab-in-modal.directive";
import { CharDataTypeNamePipe } from "rl-common/pipes/char-data-type-name.pipe";
import { MultipleIndicatorNamePipe } from "config/pipes/multiple-indicator-name.pipe";
import { RequiredIndicatorNamePipe } from "config/pipes/required-indicator-name.pipe";
import { ICreateTemplateFormData } from "../../template-fields/template-field-add/create-template-field-wizard/template-field-policy-editor/template-field-policy-editor.models";


@Component({
	selector: "rl-field-section-select-chars-modal",
	templateUrl: "./field-section-select-chars-modal.component.html",
	styleUrls: ["./field-section-select-chars-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CdkDrag, NewTabInModalDirective, RouterLink, ChipComponent, CharDataTypeNamePipe, RequiredIndicatorNamePipe, MultipleIndicatorNamePipe, CheckboxInputComponent, FormsModule, ReactiveFormsModule, TextInputComponent, AppendWithDirective]
})
export class FieldSectionSelectCharsModalComponent implements OnInit, OnDestroy {

	@Input()
	templateMetaData: ICharacteristicMetaDataCollection;

	@Input()
	selectedChars: number[] = [];

	@Input()
	groupName: string = "";

	@Output()
	onComplete = new EventEmitter<Set<number>>();

	formGroup: FormGroup<ICreateTemplateFormData>;
	isSaving = false;

	get charTypeId() {
		return this.templateMetaData.charTypeID;
	}

	get templateName() {
		return this.templateMetaData.template.templateName;
	}

	get templateId() {
		return this.templateMetaData.template.templateID;
	}

	conditionalTriggerFieldId: FormControl<number>;
	templateChars: ICharacteristicMetaData[] = [];
	filteredChars: ICharacteristicMetaData[] = [];
	fieldsToAdd = new Set<number>();
	searchControl = new FormControl("");

	hideSelected = true;
	private readonly _subs: Subscription[] = [];

	constructor() { }

	ngOnInit(): void {
		this.toggleHideSelected();
		const sub = this.searchControl.valueChanges.subscribe(() => this.applySearch());
		this._subs.push(sub);
	}

	toggleHideSelected() {
		this.hideSelected = !this.hideSelected;
		if(this.hideSelected) {
			this.templateChars = this.templateMetaData.characteristicMetaDatas.filter(cmd => !this.selectedChars.includes(cmd.characteristicID));
		} else {
			this.templateChars = this.templateMetaData.characteristicMetaDatas;
		}
		this.templateChars.sort((a, b) => a.characteristicID - b.characteristicID);
		this.applySearch();
	}

	applySearch() {
		const keywords = (this.searchControl.value ?? "").toLowerCase().trim();
		this.filteredChars = keywords ? this.templateChars.filter(cmd =>
			cmd.characteristicID.toString().includes(keywords) ||
			cmd.label?.toLowerCase().includes(keywords) ||
			cmd.tagLabel?.toLowerCase().includes(keywords)
		) : this.templateChars;
	}

	clearSearch() {
		this.searchControl.setValue("");
	}

	cancel() {
		this.onComplete.next(null);
	}

	toggleField(characteristicID: number) {
		if (this.fieldsToAdd.has(characteristicID)) {
			this.fieldsToAdd.delete(characteristicID);
		} else {
			this.fieldsToAdd.add(characteristicID);
		}
	}

	done() {
		this.onComplete.emit(this.fieldsToAdd);
	}

	getFieldById(id: number) {
		const cmd = this.templateChars.find((x) => x.characteristicID === id);
		return cmd.label;
	}

	isChecked(characteristicID: number): boolean {
		let checked = this.fieldsToAdd.has(characteristicID);
		if(!checked && !this.hideSelected) {
			checked = this.selectedChars.includes(characteristicID);
		}
		return checked;
	}

	isDisabled(characteristicID: number): boolean {
		return this.selectedChars.includes(characteristicID);
	}

	ngOnDestroy(): void {
		this._subs.forEach(sub => sub.unsubscribe());
	}
}
